import { Button, DropdownButton, DropdownMenuItem, Icon, Placeholder } from '@hai/ui-react';
import { IButtonProps, SearchInputFilter } from '@hai/ui-react/dist/types';
import { isNil } from 'ramda';
import React, { FunctionComponent, ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ActionBar, { Status } from 'src/js/component/actionBar/actionBar';
import { Comparator, MakitoObject, SortDirection, SortType } from 'src/js/component/sort-manager';
import { useStores } from 'src/js/hook/use-stores';
import { useWatchObject } from 'src/js/store/use-watch';
import { generateDataAuto } from 'src/js/util/automation';
import { constant } from 'src/js/util/constant';
import { useLocalStorage } from 'usehooks-ts';

interface StreamingActionBarProps<T extends MakitoObject> {
  items: T[];
  searchKeys?: (string | any)[];
  initialSearchFilters?: string[];
  onChangeSearchFilters?: (filters: string[]) => void;
  sortInfo: {
    [K in keyof Partial<T>]: {
      title: string;
      type: SortType;
      /*
       *  Optional
       *  Supply custom comparator function receiving model A and B as input and returning -1 | 0 | 1
       */
      comparator?: Comparator<T>;
      /*
       *  Optional
       *  Value provider for the field. Takes model as input and output value to sort on
       */
      provider?: (arg: T) => any;
    };
  };
  statusTypes?: Status[];
  getStateValue: (model: T) => string;
  actionButtons: {
    onClick: (e: any) => void | Promise<any>;
    label: string;
    counter: () => any[];
  }[];
  List: FunctionComponent<any>; // FIXME improve typing to mandate onCheckRow() and other
  updateSelection: (item: any, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
  storageKey: string;
  actionRightPrimary?: IButtonProps;
  actionRightSecondary?: IButtonProps;
  nmosServiceActive?: boolean;
  showPlaceholder?: boolean;
  scrollToId?: number | string;
  placeholderThumbnail?: boolean;
  placeholderToggles?: number;
  placeholderActions?: number;
  placeholderRightComponents?: number;
  placeholderNumPanels?: number;
}

export const StreamingActionBar = <T extends MakitoObject>(props: StreamingActionBarProps<T>) => {
  const {
    items,
    initialSearchFilters,
    onChangeSearchFilters,
    searchKeys,
    sortInfo,
    statusTypes,
    getStateValue,
    actionButtons,
    List,
    nmosServiceActive = false,
    onSelectAll,
    updateSelection,
    storageKey,
    actionRightPrimary,
    actionRightSecondary,
    scrollToId,
    showPlaceholder = true,
    placeholderThumbnail = false,
    placeholderToggles = 3,
    placeholderActions = 3,
    placeholderRightComponents = 0,
    placeholderNumPanels = 6,
  } = props;
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  // as alternative to scrollToId
  const location = useLocation();

  const sortByKey = `${constant.lStoragePrefix}${storageKey}:sortBy:${sessionStore.username}`;
  const sortDirectionKey = `${constant.lStoragePrefix}${storageKey}:sortDirection:${sessionStore.username}`;
  const [sorting, setSorting] = useLocalStorage(sortByKey, 'name');
  const [direction, setDirection] = useLocalStorage(sortDirectionKey, SortDirection.ASCENDING);

  // init safety-check routine
  if (sortInfo && !Object.prototype.hasOwnProperty.call(sortInfo, sorting)) {
    //local storage sort key is not found in our sort options, go back to default
    setSorting('name');
  }

  useEffect(() => {
    // reset all checkbox states
    return () => {
      // cleanup, so when we come back to the page, we are not flashed with a partially or fully selected list
      items.map((i) => (i.selected = false));
    };
  }, []);

  const [actionBarItems, setActionBarItems] = useState<T[]>(items);

  const trickleDownUpdates = (updates: T[]) => {
    setActionBarItems((list) => {
      updates.map((update) => {
        const item = list.find((i) => i.id == update.id); // TODO we might need some uuid in a private field for deletable objects like streams
        if (item) {
          // console.log('trickle down updates, selected', update.id, update.selected, item.selected);
          update.selected = item.selected;
          update.hidden = item.hidden;
        }
      });
      return updates;
    });
  };

  useWatchObject(items, (updates) => {
    trickleDownUpdates(updates);
  });

  // If objects are saved in a use-watch store, then the useWatchObject above is sufficient.
  // But if they are not managed by a store this routine below will take care of detecting a list of items change and trickle down the selected/hidden state.
  if (items !== actionBarItems) {
    trickleDownUpdates(items);
  }

  const onChangeSearchFiltersLocal = (filters: string | SearchInputFilter) => {
    // clear all selection on new or deleted search entry
    onChangeSearchFilters?.(filters as any);
    setActionBarItems((list) => {
      const newList = [...list];
      newList.map((item) => {
        item.selected = false;
      });
      return newList;
    });
  };

  const onSelectAllLocal = (selected: boolean | 'indeterminate' | 'none', filteredItems: any[]) => {
    setActionBarItems((list) => {
      const newList = [...list];
      newList
        .filter((i) => filteredItems.find((f) => f.id == i.id))
        .map((item) => {
          if (!item.hidden) {
            if (typeof selected === 'boolean') {
              item.selected = !selected;
            } else if (selected === 'indeterminate') {
              item.selected = false;
            }
          }
        });
      return newList;
    });

    // console.log('[StreamingActionBar] onSelectAllLocal filtered items', _filteredItems)
    onSelectAll?.(typeof selected === 'boolean' ? !selected : false); // if the top checkbox clicked was showing indeterminate, it means to unselect all
  };

  const onChangeSort = (sections: any) => {
    if (sections.sortBy?.eventKey) {
      setSorting(sections.sortBy?.eventKey);
    }

    if (sections.direction?.eventKey === 'ascending') {
      setDirection(SortDirection.ASCENDING);
    } else if (sections.direction?.eventKey === 'descending') {
      setDirection(SortDirection.DESCENDING);
    } else {
      setDirection(SortDirection.IDLE);
    }
  };

  const sortDropdown = (): ReactNode => (
    <DropdownButton
      title={sortInfo[sorting as keyof T]?.title}
      onChange={onChangeSort}
      label={
        direction === SortDirection.ASCENDING ? (
          <>
            {t('sort.sortPrefix')}
            {'\u00A0'}
            <Icon
              iconname="SortAscending"
              color="haiui-gray-06"
              size="sm3"
              data-auto={generateDataAuto('label', 'icon sort ascending')}
            />
          </>
        ) : (
          <>
            {t('sort.sortPrefix')}
            {'\u00A0'}
            <Icon
              iconname="SortDescending"
              color="haiui-gray-06"
              size="sm3"
              data-auto={generateDataAuto('label', 'icon sort descending')}
            />
          </>
        )
      }
      alignLabel="left"
      floatRight
      menuMaxHeight="none"
    >
      <DropdownMenuItem sectionHead title={t('sort.sortBy')} eventKey="sortBy" />
      {Object.keys(sortInfo).map((key) => {
        const item = sortInfo[key as keyof T];
        return <DropdownMenuItem key={key} title={item.title} eventKey={key} selected={sorting === key} />;
      })}
      <DropdownMenuItem sectionHead title={t('sort.sortOrder')} eventKey="direction" />
      <DropdownMenuItem
        title={
          <>
            {t('sort.ascending')}
            {'\u00A0'}
            <Icon iconname="SortAscending" color="haiui-gray-06" size="sm3" />
          </>
        }
        data-auto={generateDataAuto('dropdown-item', 'ascending')}
        eventKey="ascending"
        selected={direction === SortDirection.ASCENDING}
      />
      <DropdownMenuItem
        title={
          <>
            {t('sort.descending')}
            {'\u00A0'}
            <Icon iconname="SortDescending" color="haiui-gray-06" size="sm3" />
          </>
        }
        data-auto={generateDataAuto('dropdown-item', 'descending')}
        eventKey="descending"
        selected={direction === SortDirection.DESCENDING}
      />
    </DropdownButton>
  );

  // handler for selecting/unselected one item at a time
  const onSelectRow = useCallback(
    (item: T, selected: boolean) => {
      setActionBarItems(actionBarItems.map((i) => (i.id === item.id ? { ...i, selected } : i)));
      updateSelection(item, selected);
    },
    [actionBarItems],
  );

  const PlaceHolderPage = () => {
    return (
      <div className="hai-mt-1">
        <Placeholder.ListActionBar
          numToggleButtons={placeholderToggles}
          numDropdowns={1}
          numRightComponents={placeholderRightComponents}
        />
        <Placeholder.List
          numActions={placeholderActions}
          showThumbnail={placeholderThumbnail}
          numPanels={placeholderNumPanels}
        />
      </div>
    );
  };

  return (
    <>
      {showPlaceholder && items?.length === 0 ? (
        <PlaceHolderPage />
      ) : (
        <div id="streaming-actionbar">
          <ActionBar
            items={actionBarItems}
            searchKeys={searchKeys} // model keys to allow search on
            initialSearchFilters={initialSearchFilters} // user supplied search saved in localstorage
            onSearch={onChangeSearchFiltersLocal} // changing search filters
            sortDropdown={sortDropdown()}
            onSelectAll={onSelectAllLocal}
            statusTypes={statusTypes}
            getStateValue={getStateValue}
            ActionButtons={
              actionButtons.length > 0 ? (
                <>
                  {actionButtons.map((button) => (
                    <Button
                      onClick={button.onClick}
                      disabled={nmosServiceActive || button.counter().length === 0} // if 0 items in the list are matching the button, disable it
                      key={button.label}
                    >
                      {button.label}
                    </Button>
                  ))}
                </>
              ) : null
            }
            actionRightPrimary={actionRightPrimary}
            actionRightSecondary={actionRightSecondary}
          >
            {(filteredList) => {
              return (
                <>
                  {nmosServiceActive && (
                    <div className="banner-info-container">
                      <Icon iconname="Information" className="hai-mr-4" color="gray" />
                      <p className="centered-banner-info">
                        {t('essence.bannerAlert.readonly')}
                        <a href={'/settings/services'}> {t('essence.bannerAlert.viewServices')}</a>
                      </p>
                    </div>
                  )}
                  <List
                    list={filteredList}
                    sorting={sorting}
                    direction={direction}
                    onSelect={onSelectRow}
                    sortInfo={sortInfo}
                    scrollToId={scrollToId ?? location?.state?.id}
                  />
                </>
              );
            }}
          </ActionBar>
        </div>
      )}
    </>
  );
};

export interface ScrollProps {
  id: string | number;
}

export const scrollPanelIntoView = (props: ScrollProps, focus = false) => {
  const { id } = props;
  let elementId: string | number;
  if (!isNil(id)) {
    elementId = id;
  }

  if (!isNil(elementId)) {
    setTimeout(() => {
      const item = document.getElementById(`panel-${elementId}`);

      if (!isNil(item)) {
        item.scrollIntoView({ behavior: 'auto', block: 'nearest' });
        if (focus) {
          const ele: any = item.firstElementChild;
          ele.focus();
        }

        if (!isNil(id)) {
          // Clear state so that next Gear opening doesn't trigger scroll.
          window.history.replaceState({ id: null }, document.title);
        }
      }
    }, 300);
  }
};
